import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'
import { getItemsByClient } from '../../../api/inventory/get-items-by-client'

/* Initial state of the slice */
const initialState = {
  isFetchingInventoryItems: false,
  items: [],
  fetchInventoryItemsError: null,
  selectedInventoryItem: null,
}

/* Handler for making service call to fetch inventory items */
export const fetchInventoryItems = createAsyncThunk(
  'inventory/fetchInventoryItems',
  async (args, { rejectWithValue }) => {
    try {
      const response = await getItemsByClient()
      return response?.data?.data
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

/*
   - Slice:  A slice is a subset of data in the redux store
   - InitialState: The initial state of the redux slice.
   - Reducers:  Define actions here that will be dispatched to update redux state.
   - ExtraReducers: createAsyncThunk helps facilitate state updates
                    by automatically providing lifecycle hooks to
                    easily handle state updates based on the api current state.
                    This is also where you can add cases to faciliate state updates
                    when actions in other reducer slices are dispatched.
   - Immer:  Internal to RTK, converts mutable state updates to immutable so that
             we are able to easily update state directly with needing to copy state.
*/
const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setSelectedInventoryItem(state, action) {
      state.selectedInventoryItem = action.payload
    },
    resetSelectedInventoryItem(state) {
      state.selectedInventoryItem = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInventoryItems.pending, (state) => {
        state.isFetchingInventoryItems = true
        state.items = []
        state.fetchInventoryItemsError = null
      })
      .addCase(fetchInventoryItems.fulfilled, (state, action) => {
        const currentState = current(state)
        state.isFetchingInventoryItems = false
        state.items = action.payload
        if (currentState.selectedInventoryItem) {
          state.selectedInventoryItem = getRefreshedItem(
            action.payload,
            currentState.selectedInventoryItem,
          )
        }
      })
      .addCase(fetchInventoryItems.rejected, (state, action) => {
        state.isFetchingInventoryItems = false
        state.fetchInventoryItemsError = action.payload
      })
      .addCase(PURGE, () => {
        return initialState
      })
  },
})

function getRefreshedItem(updatedItems, currentSelectedItem) {
  return updatedItems.find((item) => {
    return item.itemNumber === currentSelectedItem.itemNumber
  })
}

export const { setSelectedInventoryItem, resetSelectedInventoryItem } = inventorySlice.actions

export default inventorySlice.reducer
